import { t } from 'ember-intl';
import { htmlSafe } from '@ember/template';
import { LinkTo } from '@ember/routing';
import { PhArrowSquareOut } from 'ember-phosphor-icons';
import Divider from 'tio-ui/components/divider';
import type { TOC } from '@ember/component/template-only';
import ExpandableSection from '../../approval/expandable-section.gts';
import type { TasProgramInstanceExtension as TasProgramInstanceModel } from 'tio-data/data/resources/tas-program-instance.ext';
import { DrawerHeader, DrawerBody } from 'tio-ui/components/drawer';

interface ApproverInstructionItem {
  heading: string;
  body: string;
  ordinal: number;
}

interface EmployeeFaqItem {
  heading: string;
  body: string;
  ordinal: number;
}

/**
 * Partner Info drawer for program instance approvals
 * Displays program template instructions and FAQs
 */
interface PartnerInfoDrawerSignature {
  Args: {
    instance: TasProgramInstanceModel;
    approverInstructionsItems: ApproverInstructionItem[];
    employeeFaqsItems: EmployeeFaqItem[];
    isAdminApp?: boolean;
  };
}

const PartnerInfoDrawer: TOC<PartnerInfoDrawerSignature> = <template>
  <DrawerHeader>
    <h2 class="tio-text-heading tio-fg-primary">{{t
        "tuition_assistance.application_approval.partner_information"
      }}</h2>
  </DrawerHeader>

  <DrawerBody>
    <div class="p-6">
      {{#if @instance.tasProgramTemplate}}
        {{#if @approverInstructionsItems.length}}
          <ExpandableSection
            @title={{t "tuition_assistance.application_approval.approver_instructions"}}
            @defaultExpanded={{true}}
            class="mb-8"
          >
            <ul class="list-disc ml-6 space-y-4 tio-fg-secondary text-base leading-relaxed">
              {{#each @approverInstructionsItems as |item|}}
                <li>{{htmlSafe item.body}}</li>
              {{/each}}
            </ul>
          </ExpandableSection>
          <Divider class="my-4" />
        {{/if}}

        {{#if @employeeFaqsItems.length}}
          <ExpandableSection
            @title={{t "tuition_assistance.application_approval.employee_faqs"}}
            @defaultExpanded={{true}}
            class="mb-8"
          >
            <div class="tio-col-extra-loose">
              {{#each @employeeFaqsItems as |faq|}}
                <div class="tio-col-tight">
                  <h4 class="tio-text-base-semibold tio-fg-secondary leading-snug">
                    {{htmlSafe faq.heading}}
                  </h4>
                  <p class="text-base tio-fg-secondary leading-relaxed">
                    {{htmlSafe faq.body}}
                  </p>
                </div>
              {{/each}}
            </div>
          </ExpandableSection>
        {{/if}}

        <Divider class="my-8" />

        {{#if @isAdminApp}}
          <LinkTo
            @route="authenticated.tas.program-builder"
            @model={{@instance.tasProgramTemplate.id}}
            class="tio-row-tight tio-fg-info hover:tio-fg-info-h cursor-pointer"
            data-test-view-program-info-link
          >
            <span class="tio-text-base-semibold">
              {{t "tuition_assistance.application_approval.view_program_information"}}
            </span>
            <PhArrowSquareOut @size="20" />
          </LinkTo>

          <LinkTo
            @route="authenticated.companies.show.policy-information"
            @model={{@instance.employee.company.id}}
            class="tio-row-tight tio-fg-info hover:tio-fg-info-h cursor-pointer mt-4"
            data-test-view-policy-link
          >
            <span class="tio-text-base-semibold">{{t
                "tuition_assistance.application_approval.view_policy"
              }}</span>
            <PhArrowSquareOut @size="20" />
          </LinkTo>
        {{/if}}
      {{else}}
        <p class="tio-fg-muted">{{t
            "tuition_assistance.application_approval.no_program_template_data"
          }}</p>
      {{/if}}
    </div>
  </DrawerBody>
</template>;

export default PartnerInfoDrawer;
